import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc, setDoc,
  serverTimestamp, runTransaction, query, orderBy, type Unsubscribe,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/firebase'
import type { Arrival, Category, Part, Reservation, Vehicle } from '@/types'
import { pictureFor, pictureKind, PLACEHOLDER_CHOICES, type PictureKind } from '@/lib/artwork'
import { slippedFrom } from '@/lib/arrivals'
import { stockLevel, stockState, stockLabel, canReserve, canRelease, isHolding, reservationRef } from '@/lib/stock'
import { validateReservation, normaliseMobile, normaliseEmail } from '@/lib/validate'
import { log } from '@/lib/logger'
import { deletePhotos } from '@/lib/storage'

export interface DelayResult {
  ok: true
  sent: number
  /** Holders with no email address on file: these have to be rung. */
  toCall: { name: string; mobile: string; ref: string }[]
  /** References whose email bounced at the provider. */
  failed: string[]
  daysLate: number
}

/** A category as the UI consumes it: built-ins and custom ones in one shape. */
/** What a delay notice is about: a vehicle and its parts, or one loose part. */
export interface DelayTarget {
  kind: 'vehicle' | 'part'
  id: string
}

export interface CategoryOption {
  id: string
  label: string
  fields: string[]
  image: string | null
  builtin: boolean
}

const BUILTIN_CATEGORIES: CategoryOption[] = PLACEHOLDER_CHOICES.map(([id, label]) => ({
  id, label, fields: [], image: null, builtin: true,
}))

export const useCatalogueStore = defineStore('catalogue', () => {
  const vehicles = ref<Vehicle[]>([])
  const parts = ref<Part[]>([])
  const categories = ref<Category[]>([])
  const reservations = ref<Reservation[]>([])
  const loading = ref(true)
  const error = ref<string | null>(null)

  let subs: Unsubscribe[] = []

  /** Public collections stream to everyone; reservations only to a verified admin. */
  function subscribePublic(): void {
    stop()
    loading.value = true
    subs = [
      onSnapshot(collection(db, 'vehicles'), (s) => {
        vehicles.value = s.docs.map((d) => ({ id: d.id, ...d.data() }) as Vehicle)
      }, (err) => fail('vehicles', err)),

      onSnapshot(collection(db, 'parts'), (s) => {
        parts.value = s.docs.map((d) => ({ id: d.id, ...d.data() }) as Part)
        loading.value = false
      }, (err) => fail('parts', err)),

      onSnapshot(collection(db, 'categories'), (s) => {
        categories.value = s.docs.map((d) => ({ id: d.id, ...d.data() }) as Category)
      }, (err) => fail('categories', err)),
    ]
  }

  function subscribeReservations(): void {
    subs.push(
      onSnapshot(query(collection(db, 'reservations'), orderBy('createdAt', 'desc')), (s) => {
        reservations.value = s.docs.map((d) => ({ id: d.id, ...d.data() }) as Reservation)
      }, (err) => fail('reservations', err)),
    )
  }

  function fail(what: string, err: unknown): void {
    error.value = `Could not load ${what}.`
    loading.value = false
    log.error('snapshot failed', { what, err })
  }

  function stop(): void {
    subs.forEach((u) => u())
    subs = []
  }

  /* ----------------------------- lookups ----------------------------- */

  const vehicleById = computed(() => (id: string | null) => vehicles.value.find((v) => v.id === id) ?? null)
  const partById = computed(() => (id: string) => parts.value.find((p) => p.id === id) ?? null)

  /* --------------------------- categories ---------------------------- */

  const allCategories = computed<CategoryOption[]>(() => [
    ...BUILTIN_CATEGORIES,
    ...categories.value.map((c) => ({
      id: c.id, label: c.label, fields: c.fields ?? [], image: c.image ?? null, builtin: false,
    })),
  ])
  const customCategories = computed(() => allCategories.value.filter((c) => !c.builtin))
  const categoryById = computed(() => (id: string | null | undefined) =>
    allCategories.value.find((c) => c.id === id) ?? null)
  const categoryLabel = computed(() => (id: string | null | undefined) =>
    categoryById.value(id)?.label ?? 'Part')
  const categoryFields = computed(() => (id: string | null | undefined) =>
    categoryById.value(id)?.fields ?? [])
  const categoryImage = computed(() => (id: string | null | undefined) =>
    categoryById.value(id)?.image ?? null)

  /** The picture to show for a part, including a custom category's stand-in. */
  const partPicture = computed(() => (part: Part) =>
    pictureFor(part, categoryImage.value(part.category)))
  const partPictureKind = computed(() => (part: Part): PictureKind => {
    const img = categoryImage.value(part.category)
    const src = partPicture.value(part)
    // A category stand-in is a generic image of the type, not this exact unit.
    return img && src === img ? 'reference' : pictureKind(src)
  })
  const partsInCategory = computed(() => (id: string) =>
    parts.value.filter((p) => p.category === id))

  /**
   * A part arrives with its donor vehicle; only loose stock has its own status.
   * Anything recorded before statuses existed reads as arriving with no date —
   * "soon" is the safe thing to tell a buyer, "available" would not be.
   */
  const arrivalOf = computed(() => (item: Part | Vehicle): Arrival => {
    const v = 'vehicleId' in item && item.vehicleId ? vehicleById.value(item.vehicleId) : null
    const src: Partial<Arrival> = v ?? item
    return {
      arrival: src.arrival ?? 'arriving',
      eta: src.eta ?? '',
      slippedFrom: src.slippedFrom ?? null,
      delayNotice: src.delayNotice ?? null,
    }
  })

  const partsOfVehicle = computed(() => (vehicleId: string) =>
    parts.value.filter((p) => p.vehicleId === vehicleId))

  const levelOf = computed(() => (part: Part) => stockLevel(part, reservations.value))
  const stateOf = computed(() => (part: Part) => stockState(part, reservations.value))
  const labelOf = computed(() => (part: Part) => stockLabel(part, reservations.value))

  const unitsInStock = computed(() =>
    parts.value.reduce((n, p) => n + stockLevel(p, reservations.value).available, 0))

  /** Who is still waiting on a vehicle (any part off it) or on one loose part. */
  const holdersOf = computed(() => (target: DelayTarget): Reservation[] => {
    const ids = new Set(target.kind === 'vehicle'
      ? partsOfVehicle.value(target.id).map((p) => p.id)
      : [target.id])
    return reservations.value.filter((r) => ids.has(r.partId) && isHolding(r.status))
  })

  const pendingReservations = computed(() => reservations.value.filter((r) => r.status === 'pending'))
  const decidedReservations = computed(() => reservations.value.filter((r) => r.status !== 'pending'))

  /* --------------------------- reservations -------------------------- */

  /**
   * The reference number is allocated in a transaction so two customers hitting
   * Reserve at the same instant cannot be handed the same one.
   */
  async function reserve(input: {
    partId: string; qty: number; firstName: string; lastName: string
    mobile: string; email?: string; note?: string
  }): Promise<{ ok: true; ref: string; emailed: boolean } | { ok: false; reason: string }> {
    const problems = validateReservation(input)
    if (problems.length) return { ok: false, reason: problems[0]!.message }

    const part = partById.value(input.partId)
    if (!part) return { ok: false, reason: 'That item is no longer listed.' }
    if (!canReserve(part, reservations.value, input.qty)) {
      return { ok: false, reason: `Only ${levelOf.value(part).available} left — reduce the quantity.` }
    }

    const mobile = normaliseMobile(input.mobile)!
    const email = normaliseEmail(input.email ?? '')
    try {
      const seq = await runTransaction(db, async (tx) => {
        const counter = doc(db, 'config', 'counters')
        const snap = await tx.get(counter)
        const next = ((snap.data()?.reservation as number | undefined) ?? 3041) + 1
        tx.set(counter, { reservation: next }, { merge: true })
        return next
      })

      const ref = reservationRef(seq)
      await addDoc(collection(db, 'reservations'), {
        ref,
        partId: input.partId,
        qty: Math.floor(input.qty),
        firstName: input.firstName.trim(),
        lastName: input.lastName.trim(),
        mobile,
        // Only written when given: an empty string would look like an address
        // the customer supplied, and the admin needs to know they must call.
        ...(email ? { email } : {}),
        note: (input.note ?? '').trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      })
      log.info('reservation created', { ref, partId: input.partId, qty: input.qty, emailed: Boolean(email) })
      return { ok: true, ref, emailed: Boolean(email) }
    } catch (err) {
      log.error('reservation failed', { err, partId: input.partId })
      return { ok: false, reason: 'Could not send that just now. Please try again.' }
    }
  }

  async function decide(id: string, status: 'confirmed' | 'denied'): Promise<void> {
    await updateDoc(doc(db, 'reservations', id), {
      status, decidedAt: serverTimestamp(),
    })
    log.info('reservation decided', { id, status })
  }

  /**
   * The customer changed their mind. Kept apart from `denied` because the
   * customer is told something quite different, and because the two mean
   * opposite things when you look back over a month of reservations.
   *
   * No stock is written: availability counts only pending and confirmed holds,
   * so the units come free the instant this status lands.
   */
  async function release(id: string): Promise<void> {
    const res = reservations.value.find((r) => r.id === id)
    if (!res) throw new Error('That reservation is no longer here.')
    if (!canRelease(res.status)) throw new Error('That reservation is not on hold any more.')
    await updateDoc(doc(db, 'reservations', id), {
      status: 'released', decidedAt: serverTimestamp(),
    })
    log.info('reservation released', { id, partId: res.partId, qty: res.qty })
  }

  /** Marking sold moves the units out of stock permanently. */
  async function markSold(reservationId: string): Promise<void> {
    const res = reservations.value.find((r) => r.id === reservationId)
    if (!res) return
    const part = partById.value(res.partId)
    if (!part) return
    await Promise.all([
      updateDoc(doc(db, 'parts', part.id), { sold: (part.sold || 0) + res.qty, updatedAt: serverTimestamp() }),
      updateDoc(doc(db, 'reservations', reservationId), { status: 'sold', decidedAt: serverTimestamp() }),
    ])
    log.info('reservation sold', { reservationId, partId: part.id, qty: res.qty })
  }

  /**
   * Tells everyone holding a part off a late vehicle, or a late loose part. The
   * sending lives in a Cloud Function: the browser has no mail credentials, and
   * it cannot read other people's reservations either.
   */
  async function notifyDelay(target: DelayTarget, message = '', force = false): Promise<DelayResult> {
    const call = httpsCallable<
      DelayTarget & { message: string; force: boolean }, DelayResult
    >(functions, 'notifyArrivalDelay')
    try {
      const { data } = await call({ ...target, message, force })
      log.info('delay notices requested', { ...target, sent: data.sent, toCall: data.toCall.length })
      return data
    } catch (err) {
      log.error('delay notices failed', { ...target, err })
      throw new Error(
        (err as { message?: string }).message || 'Could not send those just now. Please try again.',
      )
    }
  }

  /** Sold at the counter rather than through the site. */
  async function sellOffline(partId: string, qty = 1): Promise<void> {
    const part = partById.value(partId)
    if (!part) return
    if (levelOf.value(part).available < qty) throw new Error('Not enough free stock.')
    await updateDoc(doc(db, 'parts', partId), { sold: (part.sold || 0) + qty, updatedAt: serverTimestamp() })
    log.info('offline sale recorded', { partId, qty })
  }

  /* ----------------------------- writes ------------------------------ */

  const stamp = () => ({ updatedAt: serverTimestamp() })

  /**
   * Photos dropped during an edit are deleted from Storage only after the write
   * lands, so an abandoned edit never leaves a listing pointing at a file that
   * is no longer there.
   */
  function droppedPhotos(before: readonly (string | undefined)[], after: readonly string[]): string[] {
    return before.filter((u): u is string => Boolean(u) && !after.includes(u as string))
  }

  /** Records a pushed-back ETA (see `slippedFrom`) as the arrival fields are saved. */
  function withSlip<T extends Partial<Arrival>>(before: Partial<Arrival> | null, data: T): T {
    if (data.arrival === undefined) return data
    const next = { arrival: data.arrival, eta: data.eta ?? '' }
    return { ...data, eta: next.eta, slippedFrom: slippedFrom(before, next) }
  }

  async function saveVehicle(id: string | null, data: Partial<Vehicle>) {
    const before = id ? vehicleById.value(id) : null
    const write = withSlip(before, data)
    if (!id) return addDoc(collection(db, 'vehicles'), { ...write, createdAt: serverTimestamp(), ...stamp() })
    const result = await updateDoc(doc(db, 'vehicles', id), { ...write, ...stamp() })
    if (data.photoUrls) await deletePhotos(droppedPhotos(before?.photoUrls ?? [], data.photoUrls))
    return result
  }

  async function savePart(id: string | null, data: Partial<Part>) {
    const existing = id ? partById.value(id) : null
    // A part off a vehicle follows the vehicle, so only loose stock keeps its own.
    const write = data.vehicleId ? data : withSlip(existing?.vehicleId ? null : existing, data)
    if (!id) return addDoc(collection(db, 'parts'), { ...write, sold: 0, createdAt: serverTimestamp(), ...stamp() })
    const before = existing?.photoUrls ?? []
    const result = await updateDoc(doc(db, 'parts', id), { ...write, ...stamp() })
    if (data.photoUrls) await deletePhotos(droppedPhotos(before, data.photoUrls))
    return result
  }

  /** Deleting a vehicle takes its parts and their reservations with it. */
  async function removeVehicle(id: string): Promise<void> {
    const owned = partsOfVehicle.value(id)
    const photos = vehicleById.value(id)?.photoUrls ?? []
    for (const p of owned) await removePart(p.id)
    await deleteDoc(doc(db, 'vehicles', id))
    await deletePhotos(photos)
    log.warn('vehicle deleted', { id, partsRemoved: owned.length })
  }

  async function removePart(id: string): Promise<void> {
    const linked = reservations.value.filter((r) => r.partId === id)
    const photos = partById.value(id)?.photoUrls ?? []
    for (const r of linked) await deleteDoc(doc(db, 'reservations', r.id))
    await deleteDoc(doc(db, 'parts', id))
    await deletePhotos(photos)
    log.warn('part deleted', { id, reservationsRemoved: linked.length, photosRemoved: photos.length })
  }

  async function saveCategory(id: string | null, data: Partial<Category>) {
    const clean = { ...data, fields: (data.fields ?? []).map((f) => f.trim()).filter(Boolean) }
    if (!id) return addDoc(collection(db, 'categories'), { ...clean, createdAt: serverTimestamp(), ...stamp() })
    const before = categoryById.value(id)?.image ?? null
    const result = await updateDoc(doc(db, 'categories', id), { ...clean, ...stamp() })
    if ('image' in data && before && before !== data.image) await deletePhotos([before])
    return result
  }

  /** Refuses while any part still uses the category, so nothing is orphaned. */
  async function removeCategory(id: string): Promise<void> {
    const used = partsInCategory.value(id).length
    if (used) throw new Error(`${used} part${used === 1 ? '' : 's'} still use this category. Move them first.`)
    const image = categoryById.value(id)?.image ?? null
    await deleteDoc(doc(db, 'categories', id))
    await deletePhotos([image])
    log.warn('category deleted', { id })
  }

  async function seedCounters(): Promise<void> {
    await setDoc(doc(db, 'config', 'counters'), { reservation: 3041 }, { merge: true })
  }

  return {
    vehicles, parts, categories, reservations, loading, error,
    subscribePublic, subscribeReservations, stop,
    vehicleById, partById, arrivalOf, partsOfVehicle,
    allCategories, customCategories, categoryById, categoryLabel, categoryFields,
    categoryImage, partPicture, partPictureKind, partsInCategory,
    levelOf, stateOf, labelOf, unitsInStock, pendingReservations, decidedReservations,
    holdersOf, reserve, decide, release, markSold, sellOffline, notifyDelay,
    saveVehicle, savePart, saveCategory, removeCategory,
    removeVehicle, removePart, seedCounters,
  }
})
